import React from "react";

/**
 * SP最適化された画像バリアント構成
 *
 * サイズ:
 * - thumb:  150x150 - SPのリスト表示（グリッド/カード）
 * - small:  300x300 - SPの詳細表示
 * - medium: 600x600 - タブレット/小型デスクトップ
 * - large:  1200x1200 - 高解像度ディスプレイ（Retina）
 *
 * アイコン用:
 * - icon_small:  48x48
 * - icon_medium: 96x96
 * - icon_large:  192x192
 */

export interface ImageVariants {
  original?: string;
  thumb?: string;
  small?: string;
  medium?: string;
  large?: string;
  icon_small?: string;
  icon_medium?: string;
  icon_large?: string;
}

interface ResponsiveImageProps {
  src: string;
  variants?: ImageVariants | null;
  alt: string;
  className?: string;
  /** インラインスタイル */
  style?: React.CSSProperties;
  /** 表示サイズ: thumbnail (100w), card (150w), detail (300w), full (600w+) */
  displaySize?: "thumbnail" | "card" | "detail" | "full";
  /** loading属性: lazy (デフォルト) または eager */
  loading?: "lazy" | "eager";
  /** アイコン用かどうか */
  isIcon?: boolean;
  /** フォールバック画像URL */
  fallback?: string;
  onClick?: () => void;
}

/**
 * SP最適化されたレスポンシブ画像コンポーネント
 *
 * 使用例:
 * ```tsx
 * // カード表示（SPリスト向け）
 * <ResponsiveImage
 *   src={event.thumbnail_url}
 *   variants={event.thumbnail_variant_urls}
 *   alt={event.name}
 *   displaySize="card"
 * />
 *
 * // 詳細表示（SP詳細向け）
 * <ResponsiveImage
 *   src={product.image_url}
 *   variants={product.image_variants}
 *   alt={product.name}
 *   displaySize="detail"
 * />
 *
 * // アイコン表示
 * <ResponsiveImage
 *   src={site.icon_url}
 *   variants={site.icon_variant_urls}
 *   alt={site.name}
 *   isIcon
 * />
 * ```
 */
export const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  src,
  variants,
  alt,
  className = "",
  style,
  displaySize = "card",
  loading = "lazy",
  isIcon = false,
  fallback = "/placeholder.svg",
  onClick,
}) => {
  const handleError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const target = event.currentTarget;

    // srcsetの候補が失敗した場合、候補選択を止めて元画像へ一度だけ戻す。
    // srcだけを書き換えると、ブラウザが失敗したsrcsetを再選択し続ける。
    if (target.srcset) {
      target.srcset = "";
      target.sizes = "";
      target.src = src || fallback;
      return;
    }

    if (target.src !== new URL(fallback, window.location.href).href) {
      target.src = fallback;
    }
  };

  // バリアントがない場合は通常のimg
  if (!variants) {
    return (
      <img
        src={src || fallback}
        alt={alt}
        className={className}
        style={style}
        loading={loading}
        onClick={onClick}
        onError={handleError}
      />
    );
  }

  // srcset生成
  const srcSet = isIcon
    ? buildIconSrcSet(variants)
    : buildImageSrcSet(variants);

  // sizes属性（displaySizeに基づく）
  const sizes = isIcon
    ? "(max-width: 640px) 48px, 96px"
    : getSizesAttribute(displaySize);

  // 最適なsrc選択（フォールバック用）
  const optimizedSrc = isIcon
    ? variants.icon_small || variants.icon_medium || src
    : getOptimizedSrc(variants, displaySize) || src;

  return (
    <img
      src={optimizedSrc || fallback}
      srcSet={srcSet || undefined}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      className={className}
      style={style}
      loading={loading}
      onClick={onClick}
      onError={handleError}
    />
  );
};

/**
 * 通常画像用srcset生成
 */
function buildImageSrcSet(variants: ImageVariants): string | null {
  const parts: string[] = [];

  if (variants.thumb) parts.push(`${variants.thumb} 150w`);
  if (variants.small) parts.push(`${variants.small} 300w`);
  if (variants.medium) parts.push(`${variants.medium} 600w`);
  if (variants.large) parts.push(`${variants.large} 1200w`);

  return parts.length > 0 ? parts.join(", ") : null;
}

/**
 * アイコン用srcset生成
 */
function buildIconSrcSet(variants: ImageVariants): string | null {
  const parts: string[] = [];

  if (variants.icon_small) parts.push(`${variants.icon_small} 48w`);
  if (variants.icon_medium) parts.push(`${variants.icon_medium} 96w`);
  if (variants.icon_large) parts.push(`${variants.icon_large} 192w`);

  return parts.length > 0 ? parts.join(", ") : null;
}

/**
 * displaySizeに基づくsizes属性
 */
function getSizesAttribute(displaySize: "thumbnail" | "card" | "detail" | "full"): string {
  switch (displaySize) {
    case "thumbnail":
      // イベントカード内の当選商品画像（SPでもカード幅の約1/5）
      return "(max-width: 640px) 20vw, 100px";
    case "card":
      // SP: 1カラム=画面幅(~100vw)、タブレット以上: 2カラム=画面幅の約半分(~50vw)
      return "(max-width: 640px) 100vw, 50vw";
    case "detail":
      // SP: 画面幅、タブレット以上: 600px
      return "(max-width: 640px) 100vw, 600px";
    case "full":
      // SPでは100vw、タブレットでは600px、デスクトップでは1200px
      return "(max-width: 640px) 100vw, (max-width: 1024px) 600px, 1200px";
    default:
      return "300px";
  }
}

/**
 * displaySizeに基づく最適なsrc選択
 */
function getOptimizedSrc(
  variants: ImageVariants,
  displaySize: "thumbnail" | "card" | "detail" | "full"
): string | null {
  switch (displaySize) {
    case "thumbnail":
      return variants.thumb || variants.small || variants.original || null;
    case "card":
      return variants.small || variants.medium || variants.original || null;
    case "detail":
      return variants.small || variants.medium || variants.original || null;
    case "full":
      return variants.medium || variants.large || variants.original || null;
    default:
      return variants.small || variants.original || null;
  }
}

export default ResponsiveImage;
